import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import { Alert, Button } from '@/ui';
import { ZuulEditor } from '../../../ZuulEditor/ZuulEditor';
import _ from 'lodash';
import { usePropertySets } from '../../../../views/CustomProperties/PropertySets/queries.ts';
import ZuulLoader from '../../../ZuulLoader/ZuulLoader';
import toast from 'react-hot-toast';

interface ImportPropertySetsPopoverProps {
  setPropertySetsIds: (ids: number[]) => void;
  setPropertySetsValues: (valuesUpdater: (prev: Record<string, never>) => Record<string, never>) => void;
  propertySetsIds: number[];
  setIsOpen: (open: boolean) => void;
}

export const ImportPropertySetsPopover: React.FC<ImportPropertySetsPopoverProps> = ({
  setPropertySetsIds,
  setPropertySetsValues,
  propertySetsIds,
  setIsOpen,
}) => {
  const { data: existingPropertySets, isLoading: isPropertySetLoading } = usePropertySets();

  const [importJson, setImportJson] = useState('{}');
  const [error, setError] = useState('');

  const handleChange = (value: string) => {
    setImportJson(value);
    try {
      JSON.parse(value);
      setError('');
    } catch {
      setError('Invalid JSON format.');
    }
  };

  const handleApply = () => {
    try {
      const parsed = JSON.parse(importJson);
      const replacementKeys = Object.keys(parsed);
      const ids =
        existingPropertySets?.filter((item) => replacementKeys.includes(item.replacementKey)).map((item) => item.id) ||
        [];
      if (Array.isArray(ids)) {
        const newIds = [...new Set([...propertySetsIds, ...ids.filter((id) => id !== undefined)])];
        setPropertySetsIds(newIds);
      }

      if (parsed) {
        const merged = _.merge({}, setPropertySetsValues, parsed);
        setPropertySetsValues(merged);
      }

      toast.success('Property Sets imported and merged!');
      setIsOpen(false);
    } catch {
      setError('Failed to parse JSON.');
    }
  };

  if (isPropertySetLoading) {
    return <ZuulLoader text={' Loading Property Sets...'} />;
  }

  return (
    <Card variant='elevation' elevation={0}>
      <CardHeader
        sx={{ pb: 1 }}
        title={
          <Stack direction='row' spacing={2} sx={{ alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>{error && <Alert color='danger'>{error}</Alert>}</Box>
            <Button disabled={Boolean(error)} color='primary' onClick={handleApply}>
              Apply
            </Button>
          </Stack>
        }
        slotProps={{ title: { component: 'div' } }}
      />
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <ZuulEditor
          language='json'
          content={importJson}
          handleEditorChange={handleChange}
          height='36vh'
          validate={false}
          readOnly={false}
        />
      </CardContent>
    </Card>
  );
};
